import React from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import globalStyles from "../../global";
import { getResponsiveSize } from "../../utils/responsive";

const Footer = () => {
  const { t } = useTranslation();
  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL || "";

  const colors = {
    background: globalStyles.colors.army_green,
    text: globalStyles.colors.white,
    mutedText: "rgba(255,255,255,0.8)",
    border: "rgba(255,255,255,0.13)",
    icon: "#FFFFFF",
  };

  const fontSizes = globalStyles.fontSizes;

  // Social icons
  const socialIcons = {
    facebook: `${imageBaseUrl}fb.png`,
    instagram: `${imageBaseUrl}inst.png`,
    youtube: `${imageBaseUrl}youtube.png`,
    twitter: `${imageBaseUrl}tweet.png`,
    linkedin: `${imageBaseUrl}linkedin.png`,
    // Note: LinkedIn uses a devicon class, not an image
  };

  const contactIcons = {
    location: `${imageBaseUrl}location.png`,
    phone: `${imageBaseUrl}phon.png`,
    mail: `${imageBaseUrl}email.png`,
  };

  // Footer links and countries as seen in the image
  const leftLinks = [
     { name: "Locate Us", url: "/locate-us" },
    { name: "Media", url: "/media" },
    { name: "Career", url: "/career" },
    { name: "Blog", url: "/blog" },
    { name: "Cleaning Standards", url: "/cleaning-standards" },
    { name: "Privacy Policy", url: "/privacy" },
    { name: "Terms & Conditions", url: "/terms" },
    { name: "Contact Us", url: "/contact" }
  ];

  const countries = [
    "India",
    "Somalia",
    "Ghana",
    "UAE",
    "Nepal",
    "Sri Lanka",
    "Mauritius",
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: colors.background,
        color: colors.text,
        fontFamily: globalStyles.fonts.primary,
        width: "100%",
        pt: `${getResponsiveSize(48)}px`,
        pb: 0,
      }}
    >
      <Box
        sx={{
          maxWidth: "1240px",
          mx: "auto",
          width: "100%",
          px: { xs: 2, md: 0 },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1.3fr 1fr 1fr 1.7fr 1.2fr",
            },
            gap: {
              xs: `${getResponsiveSize(16)}px`,
              sm: `${getResponsiveSize(32)}px`,
              md: `${getResponsiveSize(56)}px`,
            },
            alignItems: "start",
            mb: `${getResponsiveSize(32)}px`,
          }}
        >
          {/* Logo & Description */}
          <Box>
            <Box sx={{ mb: `${getResponsiveSize(16)}px` }}>
              <img
                src={`${imageBaseUrl}footerlogo.png`}
                alt={t("footerhome.logoAlt")}
                style={{
                  width: `${getResponsiveSize(200)}px`,
                  height: "auto",
                  display: "block",
                }}
              />
            </Box>
            <Typography
              variant="h6"
              sx={{
                color: colors.mutedText,
                fontWeight: 350,
                fontFamily: globalStyles.fonts.primary,
                fontSize: fontSizes.h6,
                lineHeight: "24px",
                letterSpacing: "0px",
                maxWidth: 290,
                mt: 0,
              }}
            >
              {t("footerhome.companyDesc")}
            </Typography>
          </Box>

          {/* Our Countries: Links */}
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                fontFamily: globalStyles.fonts.primary,
                mb: 2,
                color: colors.text,
                fontSize: 18,
                letterSpacing: "0px",
              }}
            >
              Pages
            </Typography>
            <Box component="ul" sx={{ pl: 0, m: 0, listStyle: "none" }}>
              {leftLinks.map((link, idx) => (
                <Box
                  component="li"
                  key={idx}
                  sx={{
                    mb: 0.5,
                    fontWeight: 350,
                    fontFamily: globalStyles.fonts.primary,
                    color: colors.text,
                    fontSize: 15,
                    lineHeight: "26px",
                  }}
                >
                  <a
                    href={link.url}
                    style={{
                      color: colors.text,
                      textDecoration: "none",
                      transition: "color 0.2s",
                      fontWeight: 350,
                    }}
                    onMouseOver={e => (e.currentTarget.style.color = "#B7F2C1")}
                    onMouseOut={e => (e.currentTarget.style.color = colors.text)}
                  >
                    {link.name}
                  </a>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Our Countries: Names */}
          <Box>
            <Typography
              sx={{
                fontWeight: 500,
                fontFamily: globalStyles.fonts.primary,
                mb: 2,
                color: colors.text,
                fontSize: 18,
                letterSpacing: "0px",
              }}
                variant="h6"
            >
              Our Countries
            </Typography>
            <Box component="ul" sx={{ pl: 0, m: 0, listStyle: "none" }}>
              {countries.map((country, idx) => (
                <Box
                  component="li"
                  key={idx}
                  sx={{
                    fontWeight: 350,
                    fontFamily: globalStyles.fonts.primary,
                    color: colors.text,
                    fontSize: 15,
                    lineHeight: "26px",
                  }}
                >
                  {country}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Contact Us */}
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                fontFamily: globalStyles.fonts.primary,
                mb: `${getResponsiveSize(12)}px`,
                color: colors.text,
                fontSize: fontSizes.h5,
                letterSpacing: "0px",
              }}
            >
              {t("footerhome.contact.title")}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: `${getResponsiveSize(8)}px` }}>
              {/* Address */}
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: `${getResponsiveSize(8)}px` }}>
                <img
                  src={contactIcons.location}
                  alt="Location"
                  style={{ width: `${getResponsiveSize(18)}px`, marginTop: `${getResponsiveSize(2)}px` }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 350,
                    fontFamily: globalStyles.fonts.primary,
                    color: colors.text,
                    fontSize: fontSizes.h6,
                    lineHeight: "22px",
                    letterSpacing: "0px",
                  }}
                >
                  {t("footerhome.contact.address")}
                </Typography>
              </Box>
              {/* Phone */}
              <Box sx={{ display: "flex", alignItems: "center", gap: `${getResponsiveSize(8)}px` }}>
                <img
                  src={contactIcons.phone}
                  alt="Phone"
                  style={{ width: `${getResponsiveSize(18)}px` }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 350,
                    fontFamily: globalStyles.fonts.primary,
                    color: colors.text,
                    fontSize: fontSizes.h6,
                    lineHeight: "22px",
                    letterSpacing: "0px",
                  }}
                >
                  {t("footerhome.contact.phone")}
                </Typography>
              </Box>
              {/* Email */}
              <Box sx={{ display: "flex", alignItems: "center", gap: `${getResponsiveSize(8)}px` }}>
                <img
                  src={contactIcons.mail}
                  alt="Email"
                  style={{ width: `${getResponsiveSize(18)}px` }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 350,
                    fontFamily: globalStyles.fonts.primary,
                    color: colors.text,
                    fontSize: fontSizes.h6,
                    lineHeight: "22px",
                    letterSpacing: "0px",
                  }}
                >
                  {t("footerhome.contact.email")}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Social & Stores */}
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                fontFamily: globalStyles.fonts.primary,
                mb: `${getResponsiveSize(12)}px`,
                color: colors.text,
                fontSize: fontSizes.h5,
                letterSpacing: "0px",
              }}
            >
              Follow us
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
              <a
                href="https://www.facebook.com/ucleanindia/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "inline-flex" }}
              >
                <img
                  src={socialIcons.facebook}
                  alt="Facebook"
                  style={{ width: 24, height: 24, display: "block" }}
                />
              </a>
              <a
                href="https://instagram.com/_uclean_?utm_medium=copy_link"
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "inline-flex" }}
              >
                <img
                  src={socialIcons.instagram}
                  alt="Instagram"
                  style={{ width: 24, height: 24, display: "block" }}
                />
              </a>
              <a
                href="https://www.youtube.com/channel/UCCB8jfqaSf-UJwALz9QBHRg"
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "inline-flex" }}
              >
                <img
                  src={socialIcons.youtube}
                  alt="YouTube"
                  style={{ width: 24, height: 24, display: "block" }}
                />
              </a>
              <a
                href="https://twitter.com/ucleanindia"
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "inline-flex" }}
              >
                <img
                  src={socialIcons.twitter}
                  alt="Twitter"
                  style={{ width: 24, height: 24, display: "block" }}
                />
              </a>
               <a
                href="https://in.linkedin.com/company/uclean"
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "inline-flex" }}
              >
                <img
                  src={socialIcons.linkedin}
                  alt="Twitter"
                  style={{ width: 24, height: 24, display: "block" }}
                />
              </a>
             
            </Box>
            <Typography
              sx={{
                fontWeight: 350,
                fontFamily: globalStyles.fonts.primary,
                color: colors.text,
                fontSize: 13,
                mb: 0.5,
                lineHeight: "22px",
                letterSpacing: "0px",
              }}
            >
              Available On:
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <img
                src={`${imageBaseUrl}playstorewT.svg`}
                alt="Google Play"
                style={{
                  height: 28,
                  width: "auto",
                  borderRadius: 4,
                  background: "#fff",
                  objectFit: "contain",
                  cursor: "pointer",
                }}
                onClick={() =>
                  window.open(
                    "https://play.google.com/store/apps/details?id=com.ucleanlaundry&hl=en_IN&pli=1"
                  )
                }
              />
              <img
                src={`${imageBaseUrl}AppstorewT.svg`}
                alt="App Store"
                style={{
                  height: 28,
                  width: "auto",
                  borderRadius: 4,
                  background: "#fff",
                  objectFit: "contain",
                  cursor: "pointer",
                }}
                onClick={() =>
                  window.open("https://apps.apple.com/in/app/uclean/id1663432955")
                }
              />
            </Box>
          </Box>
        </Box>
      </Box>
      {/* Divider */}
      <Box
        sx={{
          borderTop: `1px solid ${colors.border}`,
          width: "100%",
          mt: 3,
        }}
      />
      {/* Copyright */}
      <Box
        sx={{
          maxWidth: "1240px",
          mx: "auto",
          width: "100%",
          py: 2,
          display: "flex",
          justifyContent: { xs: "center", md: "flex-start" },
        }}
      >
        <Typography
          sx={{
            color: colors.mutedText,
            fontWeight: 350,
            fontFamily: globalStyles.fonts.primary,
            fontSize: 13,
            textAlign: { xs: "center", md: "center" },
            width: "100%",
            lineHeight: "22px",
            letterSpacing: "0px",
          }}
        >
          © {new Date().getFullYear()} UClean. All rights reserved
        </Typography>
      </Box>
    </Box>
  );
};

export default Footer;